/**
 * 图像裁剪通用函数
 */

export interface Point {
  x: number;
  y: number;
}

/**
 * 根据四个角的坐标裁剪图像
 * @param imageSrc 图像源，可以是 File 对象或图像 URL
 * @param corners 四个角的坐标数组，例如 [{x:0, y:0}, {x:100, y:0}, {x:100, y:100}, {x:0, y:100}]
 * @returns 返回一个包含裁剪后图像的 Promise<Blob>
 */
export function cropImage(
  imageSrc: File | string,
  corners: [Point, Point, Point, Point]
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous"; // 允许跨域图片

    image.onload = () => {
      const xCoordinates = corners.map((p) => p.x);
      const yCoordinates = corners.map((p) => p.y);

      const minX = Math.min(...xCoordinates);
      const maxX = Math.max(...xCoordinates);
      const minY = Math.min(...yCoordinates);
      const maxY = Math.max(...yCoordinates);

      const cropX = minX;
      const cropY = minY;
      const cropWidth = maxX - minX;
      const cropHeight = maxY - minY;

      // 检查裁剪区域是否有效
      if (cropWidth <= 0 || cropHeight <= 0) {
        return reject(new Error("Invalid crop coordinates: width or height is zero or less."));
      }

      const canvas = document.createElement("canvas");
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return reject(new Error("Failed to get canvas 2D context."));
      }

      // 在 canvas 上绘制裁剪后的图像
      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      // 将 canvas 转换为 Blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create blob from canvas."));
          }
        },
        // 可以指定输出格式和质量
        imageSrc instanceof File ? imageSrc.type : 'image/png'
      );
    };

    image.onerror = () => {
      reject(new Error("Failed to load image."));
    };

    if (typeof imageSrc === "string") {
      image.src = imageSrc;
    } else {
      image.src = URL.createObjectURL(imageSrc);
    }
  });
}